<template>
  <div class="py-6">
    <div class="flex items-start justify-between">
      <LabelValue class="pr-8" :label="t('Reviews.product.view.versions.title').toString()">
        <template #hint>
          {{ t('Reviews.product.view.versions.description') }}
        </template>
      </LabelValue>
      <OpenProductProfile class="shrink-0" :slug="product.slug" />
    </div>
    <LabelValue :label="t('Reviews.product.view.pricingOptions.label').toString()">
      <div class="pb-2 text-body-m-bold">{{ t('Reviews.product.view.versions.label') }}</div>
      <ChipBadge v-if="product.freeDemo" class="mr-4" rounded :prepend-icon="iconDot" color="success" data-testid="free-demo-badge">
        {{ t('Reviews.product.view.versions.freeDemo') }}
      </ChipBadge>
      <ChipBadge v-else class="mr-4" rounded :prepend-icon="iconDotOutline" data-testid="no-free-demo-badge">
        {{ t('Reviews.product.view.versions.freeDemo') }}
      </ChipBadge>
      <ChipBadge v-if="product.freeTrial" rounded :prepend-icon="iconDot" color="success" data-testid="free-trial-badge">
        {{ t('Reviews.product.view.versions.freeTrial') }} ({{ trialPeriod }})
      </ChipBadge>
      <ChipBadge v-else rounded :prepend-icon="iconDotOutline" data-testid="no-free-trial-badge">
        {{ t('Reviews.product.view.versions.freeTrial') }}
      </ChipBadge>

      <div class="mt-4 pb-1 text-body-m-bold">
        {{ t('Reviews.product.view.disclaimer.label') }}
      </div>
      <BtnLink
        v-if="product.showDisclaimer && product.disclaimerUrl"
        class="text-body-s-bold underline"
        :to="product.disclaimerUrl"
        data-testid="disclaimer-url"
      >
        {{ product.disclaimerText }}
      </BtnLink>
      <div v-else-if="product.showDisclaimer" data-testid="disclaimer-text">
        {{ product.disclaimerText }}
      </div>
      <div v-else>{{ t('Reviews.product.view.disclaimer.missing') }}</div>
    </LabelValue>

    <LabelValue v-if="product.noPricingPlans" :label="t('Reviews.product.view.noPricing.label').toString()">
      <div class="pb-1 text-body-m-bold">{{ t('Reviews.product.view.disclaimer.label') }}</div>
      <div data-testid="no-pricing-hint">{{ product.noPricingPlansHint }}</div>
    </LabelValue>

    <LabelValue v-else :label="t('Reviews.product.view.pricingPlans.label').toString()">
      <PricingPlansPreview v-if="product.pricingPlans.length" :pricing-plans="product.pricingPlans" />
      <ReviewsEmptyView
        v-else
        class="py-8"
        :title="t('Reviews.product.view.pricingPlans.noPricingPlans')"
        :description="t('Reviews.product.view.pricingPlans.noPricingPlansInfo')"
        :action="{
          label: t('Reviews.product.view.pricingPlans.addPricing'),
          to: { name: ReviewsRouteName.ReviewsProductEditPricing, params: { productId: product.id } },
        }"
      >
        <template #image>
          <NoPricingIcon />
        </template>
      </ReviewsEmptyView>
    </LabelValue>
  </div>
</template>
<script setup lang="ts">
import { BtnLink, ChipBadge, iconDot, iconDotOutline } from '@ramp106/omrjs-core-ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import ReviewsEmptyView from '@/components/ReviewsEmptyView.vue'
import OpenProductProfile from '@/components/ReviewsManagement/OpenProductProfile.vue'
import type { ProductById } from '@/gql/reviews'
import { ReviewsRouteName } from '@/router/types'
import LabelValue from '@/ui/LabelValue.vue'

import NoPricingIcon from './NoPricingIcon.vue'
import PricingPlansPreview from './PricingPlansPreview.vue'

const props = defineProps<{
  product: ProductById
}>()

const { t } = useI18n()

const trialPeriod = computed(() => {
  const timeUnit = t(`Reviews.product.timeUnit.${props.product.trialUnit}`, props.product.trialPeriod ?? 2)
  return `${props.product.trialPeriod} ${timeUnit}`
})
</script>
